import { DateTime, Duration } from 'luxon';
import { APIConfig } from 'src/config';
import {
  P1D, P1M, P1M1D, PT30M, TIME_ZONE_SYSTEM,
} from 'src/util/constants';

/**
 * Transform a date implemented as a DateTime in local time, to
 * a timestamp in DateTime in the given preferred timezone.
 * @param {(DateTime|null)} date
 * @param {string} tz name of timezone
 * @returns {(DateTime|null)} the time in the timezone.
 */
export const dateToTimeInTimezone = (date, tz) => {
  if (!date) { return null; }

  const timezone = tz || APIConfig().DEFAULT_TIMEZONE;

  return DateTime.fromObject(
    { year: date.year, month: date.month, day: date.day },
    { zone: timezone },
  );
};

/**
 * Returns the timezone to use, based on following priority order:
 * 1. Explicit from the URL
 * 2. Implicit from the property (if applicable)
 * 3. Implicit from config
 * 4. Implicit from system
 * @param {string} timezone
 * @returns {string} The timezone to use.
 */
export const getTimezone = (timezone = '') => {
  const searchParams = new URLSearchParams(window.location.search);
  const requestedTimezone = searchParams.get('tz');
  if (requestedTimezone) {
    return requestedTimezone;
  }

  return timezone || APIConfig().DEFAULT_TIMEZONE || TIME_ZONE_SYSTEM;
};

/**
 * For a given date range a finish date is represented as midnight of the day in our date range
 * object, however as a time range it is considered at 24:00:00 that is midnight the following day.
 * A day is a 24 hours interval (not withstanding daylight saving and/or leap seconds). Hence the
 * addition of one day.
 * @param {object} dateRange
 * @param {DateTime} dateRange.start
 * @param {DateTime} dateRange.finish
 * @param {string} tz
 * @returns {object} - time range.
 */
export const dateRangeToTimeRange = (dateRange, tz) => {
  if (!dateRange) return null;

  const timezone = tz || APIConfig().DEFAULT_TIMEZONE;
  const { start: startDate, finish: finishDate } = dateRange;

  const start = dateToTimeInTimezone(startDate, timezone);
  const finish = dateToTimeInTimezone(finishDate, timezone)?.plus({ days: 1 });

  return { start, finish };
};

/**
 * Returns the aggregation for the chart in dashboard (property & portfolio)
 * @param {object} start
 * @param {object} finish
 * @returns {P1D | PT30M} - aggregation
 */
const getAggregation = (start, finish) => {
  let agg = PT30M;
  if (!start || !finish) {
    return agg;
  }
  const duration = finish.diff(start);
  if (duration.as('days') > 7) {
    agg = P1D;
  }
  return agg;
};

/**
 * Returns the time span and aggregation for the dashboard (property & portfolio)
 * @param {number} start
 * @param {number} finish
 * @param {string} timezone
 * @returns {object} - time span and aggregation
 */
export const getDashboardTimeScope = (start, finish, timezone) => {
  let s = DateTime.local({ zone: timezone }).startOf('day').minus(Duration.fromISO(P1M1D));
  if (start && timezone) {
    const ts = DateTime.fromSeconds(start, { zone: timezone });
    if (ts.invalidExplanation === null) {
      s = ts;
    }
  }

  let f = s.plus(Duration.fromISO(P1M));
  if (finish && timezone) {
    const ts = DateTime.fromSeconds(finish, { zone: timezone });
    if (ts.invalidExplanation === null) {
      f = ts.minus(Duration.fromISO(P1D));
    }
  }
  const agg = getAggregation(s, f);

  return { s, f, agg };
};

/**
 * Check whether the timespan has changed.
 * Compares between the proposed timespan and the current timespan.
 * If the dates are the same, logs a message and returns `false`; otherwise, returns `true`.
 * @param {DateTime} inputStart - The proposed start time of the timespan.
 * @param {DateTime} inputFinish - The proposed finish time of the timespan.
 * @param {object} currentTimespan - The current timespan to compare against.
 * @returns {boolean} - returns true if the timespan is valid and false if not.
 */
export const timespanChanged = (inputStart, inputFinish, currentTimespan) => {
  // Validate inputs
  if (!inputStart || !inputFinish || !inputStart.isValid || !inputFinish.isValid) {
    console.error('Invalid date inputs to handleTimespanUpdate:', { inputStart, inputFinish });
    return false;
  }

  // Check if this is the same as the current datespan to avoid unnecessary updates
  const isSameStart = inputStart.toMillis() === currentTimespan.start.toMillis();
  const isSameFinish = inputFinish.toMillis() === currentTimespan.finish.toMillis();

  if (isSameStart && isSameFinish) {
    console.log('Timespan update skipped - dates are identical');
    return false;
  }
  return true;
};

/**
 * Sets the timezone and start of day for the given timespan.
 * @param {DateTime} time - The time to modify.
 * @param {string} timezone - The timezone to use.
 * @returns {DateTime} - modified timespan.
 */
export const timeInZoneAtStartOfDay = (time, timezone) => {
  if (!time || !timezone) {
    return time;
  }
  return time.setZone(timezone).startOf('day');
};

/**
 * Converts a variety of time representations to epoch milliseconds.
 * @param {number|Date|{toMillis: function(): number}|null|undefined} v - The value to convert.
 * @returns {number|null} Epoch milliseconds or null if invalid.
 */
export const toMillis = (v) => {
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'object' && v !== null && 'toMillis' in v && typeof v.toMillis === 'function') {
    const n = v.toMillis();
    return Number.isFinite(n) ? n : null;
  }
  return null;
};
